import { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Container,
  Paper,
  Typography,
  TextField,
  Button,
  Box,
  Alert,
  CircularProgress
} from '@mui/material';
import EmailIcon from '@mui/icons-material/Email';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import authService from '../../services/authService';
import { validators } from '../../utils/validators';
import alertify from 'alertifyjs';

/**
 * ForgotPasswordPage - Request password reset email
 * 
 * User enters email → Backend sends reset link → Success message
 */
const ForgotPasswordPage = () => {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [emailSent, setEmailSent] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    // Validate email
    const emailError = validators.email(email);
    if (emailError) {
      setError(emailError);
      alertify.error(emailError);
      return;
    }

    setLoading(true);

    try {
      const response = await authService.forgotPassword(email);
      
      alertify.success(response.message || 'Şifre sıfırlama linki gönderildi');
      setEmailSent(true);
    } catch (err) {
      const errorMessage = err.response?.data?.message || 'Bir hata oluştu. Lütfen tekrar deneyin.';
      setError(errorMessage);
      alertify.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  // Success state
  if (emailSent) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          bgcolor: 'grey.100',
          py: 4
        }}
      >
        <Container maxWidth="sm">
          <Paper elevation={3} sx={{ p: 4, textAlign: 'center' }}>
            <CheckCircleIcon sx={{ fontSize: 80, color: 'success.main', mb: 3 }} />
            
            <Typography variant="h4" gutterBottom sx={{ fontWeight: 700 }}>
              Email Gönderildi! ✅
            </Typography>
            
            <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
              <strong>{email}</strong> adresine şifre sıfırlama linki gönderdik.
            </Typography>
            
            <Alert severity="info" sx={{ mb: 3, textAlign: 'left' }}>
              <Typography variant="body2" sx={{ mb: 1 }}>
                <strong>Lütfen dikkat:</strong>
              </Typography>
              <Typography variant="body2">
                • Email gelmediyse spam klasörünüzü kontrol edin
              </Typography>
              <Typography variant="body2">
                • Link 10 dakika geçerlidir
              </Typography>
              <Typography variant="body2">
                • Linke tıklayarak yeni şifrenizi oluşturabilirsiniz
              </Typography>
            </Alert>
            
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Button
                variant="outlined"
                startIcon={<ArrowBackIcon />}
                component={Link}
                to="/login"
                size="large"
              >
                Giriş Sayfasına Dön
              </Button>
              
              <Button
                variant="text"
                onClick={() => {
                  setEmailSent(false);
                  setEmail('');
                }}
                size="small"
              >
                Farklı email ile tekrar dene
              </Button>
            </Box>
          </Paper>
        </Container>
      </Box>
    );
  }

  // Email form
  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        bgcolor: 'grey.100',
        py: 4
      }}
    >
      <Container maxWidth="sm">
        <Paper elevation={3} sx={{ p: 4 }}>
          {/* Header */}
          <Box sx={{ textAlign: 'center', mb: 4 }}>
            <EmailIcon sx={{ fontSize: 60, color: 'primary.main', mb: 2 }} />
            <Typography variant="h4" gutterBottom sx={{ fontWeight: 700 }}>
              Şifremi Unuttum
            </Typography>
            <Typography variant="body1" color="text.secondary">
              Email adresinizi girin, size şifre sıfırlama linki gönderelim
            </Typography>
          </Box>

          {/* Error Alert */}
          {error && (
            <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError('')}>
              {error}
            </Alert>
          )}

          {/* Form */}
          <form onSubmit={handleSubmit}>
            <TextField
              fullWidth
              label="Email Adresiniz"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              error={!!error}
              disabled={loading}
              autoComplete="email"
              autoFocus
              margin="normal"
              placeholder="ornek@email.com"
              helperText="Kayıtlı email adresinizi girin"
            />

            <Button
              fullWidth
              type="submit"
              variant="contained"
              size="large"
              disabled={loading || !email}
              startIcon={loading ? <CircularProgress size={20} /> : <EmailIcon />}
              sx={{ mt: 3, mb: 2, py: 1.5 }}
            >
              {loading ? 'Gönderiliyor...' : 'Şifre Sıfırlama Linki Gönder'}
            </Button>
          </form>

          {/* Back to Login */}
          <Box sx={{ textAlign: 'center', mt: 3 }}>
            <Link to="/login" style={{ textDecoration: 'none' }}>
              <Button
                variant="text"
                startIcon={<ArrowBackIcon />}
                sx={{ color: 'text.secondary' }}
              >
                Giriş sayfasına dön
              </Button>
            </Link>
          </Box>
        </Paper>
      </Container>
    </Box>
  );
};

export default ForgotPasswordPage;

